import {
  InterviewAccessPurpose,
  InterviewEntryPage,
  type CandidateWebCallPayload,
  type InterviewEntryReads,
  type InterviewSessionResponse,
} from 'api/generated/types';
import { getCall } from 'api/generated/api/call';
import { getCampaigns } from 'api/generated/api/campaigns';
import { getLanguages } from 'api/generated/api/languages';
import { getScheduledAiWebCalls } from 'api/generated/api/scheduled-ai-web-calls';
import { checkWebLinkWithRetry } from 'api/checkWebLinkWithRetry';
import { webCallSettingsKey } from 'hooks/transcriptKeys';
import type { CandidateRouteKey } from 'candidate-url-registry/registry';
import { registerExternalAgentInterviewTransport } from 'pages/external-agent-chat/interviewTransport';
import {
  isCandidateActionLink,
  opensSchedulingPage,
} from 'pages/web-call/intake/linkResolutionProjection';
import { getInFlightMarker } from 'pages/web-call/session/inFlightInterviewMarker';
import {
  WEB_CALL_INITIAL_ACTIONS,
  type WebCallInitialAction,
} from 'pages/web-call/session/webCallSessionTypes';
import { parseOneOf } from 'utils/typeGuards';

import {
  LANGUAGE_REGISTRY_REQUEST_KEY,
  bootLanguageRegistry,
  bootLinkCheck,
  bootLinkQuestions,
  bootScheduledInterview,
  bootUninterestedInfo,
  bootWebCallSettings,
  bootWithdrawalInfo,
  linkCheckRequestKey,
  schedulingLinkCheckPayload,
} from './bootRequests';
import {
  captureCandidateContext,
  deriveCandidateCredential,
  ensureCandidateSession,
  getCurrentCandidateCredential,
  type CandidateContext,
} from './candidateCapability';
import {
  candidateRouteChunks,
  preloadCandidateRouteChunk,
  warmCandidateRouteChunk,
  type CandidateRouteChunk,
  type CandidateRouteMatch,
} from './preloadCandidateRouteChunk';
import {
  loadAlternativeRole,
  loadInterviewScheduling,
  loadPreapplyOptOut,
  loadRescheduleInterview,
  loadRescheduleReason,
  loadResumeFeedbackDetails,
  loadUninterested,
  loadWebCall,
  loadWithdrawal,
} from './routeChunks';
import { normalizeLangParam } from '../i18nLocaleLoader';

// The invite and shared-apply landing page, /web-interview/:encodedId (and the
// legacy /web-call/:encodedId), where WebCallPage resolves the link.
const LINK_LANDING_RE = /^\/(?:web-interview|web-call)\/([^/]+)\/?$/;

// Pages that read the language registry, including every interview entry route
// and the emailed token pages (useCandidateLanguageMode).
const LANGUAGE_REGISTRY_PAGES: ReadonlySet<CandidateRouteChunk> = new Set([
  loadInterviewScheduling,
  loadRescheduleReason,
  loadRescheduleInterview,
  loadWebCall,
  loadUninterested,
  loadWithdrawal,
  loadAlternativeRole,
  loadPreapplyOptOut,
  loadResumeFeedbackDetails,
]);

// WebCallPage on the invitation itself.
const LANDING_ROUTES: ReadonlySet<CandidateRouteKey> =
  new Set<CandidateRouteKey>(['WEB_INTERVIEW', 'WEB_CALL_LEGACY']);
// InterviewSchedulingPage before booking, on the invitation or in its session.
const SCHEDULING_ROUTES: ReadonlySet<CandidateRouteKey> =
  new Set<CandidateRouteKey>([
    'WEB_INTERVIEW_SCHEDULE',
    'WEB_INTERVIEW_SCHEDULE_CONFIRM',
    'WEB_CALL_LEGACY_SCHEDULE',
    'WEB_CALL_LEGACY_SCHEDULE_CONFIRM',
    'WEB_INTERVIEW_SESSION_SCHEDULE',
    'WEB_INTERVIEW_SESSION_SCHEDULE_CONFIRM',
  ]);

const rendersLanguageRegistry = (key: CandidateRouteKey): boolean => {
  const chunk = candidateRouteChunks[key];
  return !chunk || LANGUAGE_REGISTRY_PAGES.has(chunk);
};

/** A read the exchange returned, else the request that reads it. */
const readOrSend = <T>(
  read: T | null | undefined,
  send: () => Promise<T>
): Promise<T> => (read ? Promise.resolve(read) : send());

/** The job's settings in `uiLang`, as useWebCallCampaignSettings reads them. */
function startSettings(
  session: InterviewSessionResponse,
  uiLang: string,
  candidateContext: CandidateContext | null,
  read: InterviewEntryReads['settings']
): void {
  const jobId = session.bootstrap.job_id;
  const params = { uiLang };
  bootWebCallSettings.start(
    JSON.stringify(webCallSettingsKey(jobId, params, session.session_id)),
    () =>
      readOrSend(read, () =>
        getCall().callGetCampaignSettingsForWebCall(jobId, params, {
          candidateContext,
        })
      )
  );
}

/**
 * The landing page's link check, settings and intake questions. The bootstrap's
 * `job_id` is the `campaign_id` check_web_link returns, so none waits for the
 * link check.
 */
function startLanding(
  credential: CandidateContext,
  encodedId: string | null,
  uiLang: string,
  initialAction: WebCallInitialAction | null
): void {
  let reads: InterviewEntryReads | null = null;
  ensureCandidateSession(
    credential,
    encodedId
      ? {
          uiLang,
          onReads: (entryReads) => {
            reads = entryReads;
          },
        }
      : undefined
  ).then(
    (session) => {
      if (
        !encodedId ||
        (session.purpose !== InterviewAccessPurpose.interview &&
          session.purpose !== InterviewAccessPurpose.application_entry)
      ) {
        return;
      }
      const candidateContext = captureCandidateContext();
      // useLinkResolution's payload: the call is named only for an invite.
      const payload: CandidateWebCallPayload = {
        webCallEncodedId: encodedId,
        callId:
          session.purpose === InterviewAccessPurpose.interview
            ? session.bootstrap.call_id
            : undefined,
      };
      bootLinkCheck.start(linkCheckRequestKey(payload), () => {
        const linkCheck = readOrSend(reads?.link_check, () =>
          checkWebLinkWithRetry(payload, { candidateContext })
        );
        // A job that books its interviews sends the candidate on to the
        // scheduling page, whose chunk can download meanwhile.
        linkCheck.then(
          (response) => {
            if (
              opensSchedulingPage(response, {
                sessionPurpose: session.purpose,
                isCandidateActionLink: isCandidateActionLink(initialAction),
                inFlightMarker: getInFlightMarker,
              })
            )
              warmCandidateRouteChunk(loadInterviewScheduling);
          },
          () => {
            // swallow-ok: the landing page takes this request and handles its failure
          }
        );
        return linkCheck;
      });

      startSettings(session, uiLang, candidateContext, reads?.settings);
      const jobId = session.bootstrap.job_id;
      bootLinkQuestions.start(jobId, () =>
        readOrSend(reads?.questions, () =>
          getCampaigns().campaignsGetCampaignPermanentLinkQuestions(jobId, {
            candidateContext,
          })
        )
      );
    },
    () => {
      // swallow-ok: RouteWrapper awaits the same exchange (or retries it) and renders its failure
    }
  );
}

/**
 * The scheduling page's link check and settings (useSchedulingCampaignDisplay).
 * `linkId` is the invitation in the path, absent on the session's own route;
 * `entryEncodedId` is set when the exchange is for that invitation, which
 * then returns both reads.
 */
function startScheduling(
  credential: CandidateContext,
  linkId: string | undefined,
  entryEncodedId: string | null,
  uiLang: string
): void {
  let reads: InterviewEntryReads | null = null;
  ensureCandidateSession(
    credential,
    entryEncodedId
      ? {
          uiLang,
          page: InterviewEntryPage.scheduling,
          onReads: (entryReads) => {
            reads = entryReads;
          },
        }
      : undefined
  ).then(
    (session) => {
      // InterviewSchedulingPage renders nothing for another purpose.
      if (session.purpose !== InterviewAccessPurpose.interview) return;
      const candidateContext = captureCandidateContext();
      const payload = schedulingLinkCheckPayload(
        session.bootstrap.call_id,
        linkId
      );
      if (payload.webCallEncodedId || payload.callId) {
        bootLinkCheck.start(linkCheckRequestKey(payload), () =>
          readOrSend(reads?.link_check, () =>
            checkWebLinkWithRetry(payload, { candidateContext })
          )
        );
      }
      startSettings(session, uiLang, candidateContext, reads?.settings);
    },
    () => {
      // swallow-ok: RouteWrapper awaits the same exchange (or retries it) and renders its failure
    }
  );
}

/** RescheduleReasonPage's scheduled interview and its job's settings. */
function startReschedule(
  credential: CandidateContext,
  interviewId: string,
  uiLang: string
): void {
  ensureCandidateSession(credential).then(
    (session) => {
      // RouteWrapper shows a recovery session its recovery screen instead.
      if (session.purpose === InterviewAccessPurpose.recovery) return;
      const candidateContext = captureCandidateContext();
      bootScheduledInterview.start(interviewId, () =>
        getScheduledAiWebCalls().scheduledAiWebCallsGetScheduledAiWebCall(
          interviewId,
          undefined,
          { candidateContext }
        )
      );
      startSettings(session, uiLang, candidateContext, null);
    },
    () => {
      // swallow-ok: RouteWrapper awaits the same exchange (or retries it) and renders its failure
    }
  );
}

/**
 * The info read of an emailed token page (useCandidateTokenResource), sent as
 * the page sends it: the path's token is both the resource and its
 * `candidate-token` credential.
 */
function startTokenPageRead(route: CandidateRouteMatch): void {
  const token = route.params.token;
  if (!token) return;
  const options = { headers: { 'candidate-token': token } };
  if (route.key === 'CANDIDATE_UNINTERESTED') {
    bootUninterestedInfo.start(token, () =>
      getCampaigns().campaignsGetUninterestedInfo(token, options)
    );
  } else if (route.key === 'CANDIDATE_WITHDRAW') {
    bootWithdrawalInfo.start(token, () =>
      getCampaigns().campaignsGetWithdrawalInfo(token, options)
    );
  }
}

/**
 * The public language registry, a task later so that the exchange this boot
 * starts, which the page needs first, goes out first.
 */
function startLanguageRegistry(): void {
  setTimeout(() => {
    bootLanguageRegistry.start(LANGUAGE_REGISTRY_REQUEST_KEY, () =>
      getLanguages().languagesGetLanguageRegistry({ candidateContext: null })
    );
  }, 0);
}

/**
 * Starts the interview link's API requests from src/index.tsx, while the
 * app's code is still downloading, instead of one after another once the page
 * has rendered.
 *
 * The session exchange (POST /candidate_capability) goes first: RouteWrapper's
 * ensureCandidateSession for the same credential joins it rather than sending
 * its own. On the invitation's landing and scheduling pages the exchange also
 * asks for the page's first reads, which the server returns with the session,
 * so they cost no further round trip. A read it does not return (a reused
 * session in a reloaded tab, an external agent's transport, a failed read) is
 * requested once the session resolves, as are the session's scheduling page's
 * and the reschedule page's reads. A lazy page's chunk starts downloading
 * before the exchange, including the invitation's landing page chunk.
 * The not-interested and withdrawal pages' info read starts with the page
 * chunk. The public language registry follows on every page that
 * reads it. The page takes each request through bootRequests.ts; a request
 * whose inputs no longer match what the page would send is left unused and
 * the page sends its own.
 *
 * Candidates on other routes, practice and staff test sessions, and recovery
 * sessions get only the session exchange.
 */
export function startInterviewBootRequests(): void {
  registerExternalAgentInterviewTransport();
  const { pathname, search } = window.location;
  const credential = getCurrentCandidateCredential();
  const query = new URLSearchParams(search);
  const lang = query.get('lang');
  // The language i18n starts in, which the pages read settings in.
  const uiLang = lang ? normalizeLangParam(lang) : 'en';
  // WebCallPage's `?action=`, which keeps the landing page on the intake form.
  const initialAction =
    parseOneOf(WEB_CALL_INITIAL_ACTIONS, query.get('action')) ?? null;
  if (
    credential?.kind === 'web_call_link' &&
    LINK_LANDING_RE.exec(pathname)?.[1] === credential.encoded_id
  ) {
    preloadCandidateRouteChunk(pathname);
    startLanding(credential, credential.encoded_id, uiLang, initialAction);
    startLanguageRegistry();
    return;
  }
  // RouteWrapper only asks for the invitation here (an older email's link).
  if (!credential && pathname.startsWith('/reschedule/')) return;
  const route = preloadCandidateRouteChunk(pathname);
  if (route) startTokenPageRead(route);
  if (route && rendersLanguageRegistry(route.key)) startLanguageRegistry();
  if (!credential) return;
  // The path's invitation, when it is the credential, as the path spells it.
  // The scheduling page's link check names the router's decoded param.
  const pathInvitation = deriveCandidateCredential(pathname, null);
  const invitation =
    credential.kind === 'web_call_link' &&
    pathInvitation?.kind === 'web_call_link' &&
    pathInvitation.encoded_id === credential.encoded_id
      ? credential.encoded_id
      : null;
  if (route && LANDING_ROUTES.has(route.key)) {
    startLanding(credential, invitation, uiLang, initialAction);
    return;
  }
  if (route && SCHEDULING_ROUTES.has(route.key)) {
    startScheduling(credential, route.params.encodedId, invitation, uiLang);
    return;
  }
  const interviewId =
    route?.key === 'RESCHEDULE' ? route.params.interviewId : undefined;
  if (interviewId) {
    startReschedule(credential, interviewId, uiLang);
    return;
  }
  ensureCandidateSession(credential).catch(() => {
    // swallow-ok: RouteWrapper awaits the same exchange (or retries it) and renders its failure
  });
}
